/* eslint-disable no-unused-vars */
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';
import { showFormErrorFeedback } from '../../lib/formFeedback';
import { computeTtcFromHtAndTaxes, normalizeTaxesSource } from '../../lib/computeTtc';
import { API } from './constants';

export function useSouscriptionActions(deps) {
  const {
    contratParent, eligibilityForm, formData, getActivitesSelectionneesFull, getMontantsFinals, getTaxesFinals,
    isSuperAdmin, location, membreData, modeCalcul, navigate, operationNominative,
    propositionConversion, resultat, resumeParentDevisId, selectedActivites2nd, selectedActivitesGO, selectedActivitesPIB,
    selectedActivitesTP, setLoading, setPropositionConversion, user, validitePersonnalisee,
  } = deps;

  const handleSouscrireFromProposition = () => {
    if (!propositionConversion) return;

    // Reconstituer le state à partir du sessionStorage (snapshot complet).
    // Fallback sur location.state si sessionStorage est indisponible.
    let stateFromSnapshot = null;
    try {
      const raw = sessionStorage.getItem(
        `batico_proposition_${propositionConversion.devisId}`
      );
      if (raw) stateFromSnapshot = JSON.parse(raw);
    } catch (_) {
      stateFromSnapshot = null;
    }

    // Auto-détection du workflow selon le rôle :
    //   - beneficiaire_pro / member → /member/souscription
    //   - cooper / admin / super_admin / expert → /cooper/souscription-annuelle
    //   - public (sans user) → /login (avec retour planifié)
    const role = user?.role;
    let targetPath = '/cooper/souscription-annuelle';
    if (role === 'beneficiaire_pro' || role === 'member') {
      targetPath = '/member/souscription';
    } else if (!role) {
      toast.info('Connectez-vous pour finaliser votre souscription.');
      navigate('/login', {
        state: { redirect_after_login: '/calculateur-batico' }
      });
      return;
    }

    // Naviguer en passant le snapshot via location.state ET en URL
    // (pour traçabilité dans les logs / analytics).
    navigate(
      `${targetPath}?proposition=${propositionConversion.devisId}&from=batico`,
      { state: stateFromSnapshot || {} }
    );
  };

  const handleSaveForLater = async () => {
    if (!propositionConversion) return;
    const devisId = propositionConversion.devisId;
    let snapshot = null;
    try {
      const raw = sessionStorage.getItem(`batico_proposition_${devisId}`);
      if (raw) snapshot = JSON.parse(raw);
    } catch (_) {
      snapshot = null;
    }

    try {
      const token = getToken();
      if (token) {
        await axios.post(
          `${API}/batico/proposition/persist-and-email`,
          {
            devis_id: devisId,
            validite_jours: propositionConversion.validiteJours || 30,
            snapshot,
            membre_id: membreData?.membre_id || membreData?.id || null,
            membre_email: membreData?.email || null,
            membre_name: (
              membreData?.raison_sociale
              || (membreData?.prenom && membreData?.nom
                ? `${membreData.prenom} ${membreData.nom}`
                : null)
            ),
            parent_devis_id: resumeParentDevisId || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Proposition enregistrée', {
          description: `Vous pourrez la reprendre depuis votre tableau de bord (#${devisId.slice(0, 8)}…).`,
          duration: 5000,
        });
      } else {
        // Pas de session : on garde au moins le sessionStorage local
        toast.info('Proposition gardée dans cette session', {
          description: 'Connectez-vous pour la sauvegarder durablement.',
        });
      }
    } catch (err) {
      // Best-effort : si la persistance échoue, on ne bloque pas l'utilisateur.
      // La bannière sessionStorage couvre encore la session courante.
      // eslint-disable-next-line no-console
      console.warn('persist-and-email failed for save-for-later:', err);
      toast.warning('Proposition gardée localement', {
        description: 'Sauvegarde serveur indisponible — réessayez plus tard depuis le PDF.',
      });
    } finally {
      setPropositionConversion(null);
    }
  };

  const handleSouscrire = () => {
    // Vérifier qu'on a bien un résultat de calcul
    if (!resultat) {
      toast.error('Veuillez d\'abord calculer votre tarif');
      return;
    }

    // Mode NOMINATIVE - Membres, COOPER'S, Admin et Super Admin
    if (modeCalcul === 'nominative') {
      // Tous les rôles autorisés utilisent le même workflow de souscription annuelle
      if (user && (user.role === 'member' || user.role === 'cooper' || user.role === 'admin' || user.role === 'super_admin')) {
        const montantsFinals = getMontantsFinals();
        const taxesFinals = getTaxesFinals();
        const tarificationMappee = {
          montant_total_ht: montantsFinals?.montant_ht || resultat.montant_ht || 0,
          montant_ht: montantsFinals?.montant_ht || resultat.montant_ht || 0,
          montant_ttc: montantsFinals?.montant_ttc || resultat.montant_ttc || 0,
          montant_ht_gros_oeuvre: resultat.detail_calcul?.rcd_solidaire_gros_oeuvre || 0,
          montant_ht_second_oeuvre: resultat.detail_calcul?.rcd_solidaire_second_oeuvre || 0,
          montant_ht_travaux_publics: resultat.detail_calcul?.rcd_solidaire_travaux_publics || 0,
          montant_ht_pib: resultat.detail_calcul?.rcd_solidaire_pib_net || 0,
          taxes: taxesFinals?.is_personnalise ? taxesFinals : (resultat.taxes || { tva: (montantsFinals?.montant_ht || resultat.montant_ht || 0) * 0.1 }),
          detail_calcul: resultat.detail_calcul || {},
          ...(montantsFinals?.is_personnalise && {
            montants_personnalises: {
              ca_personnalise: montantsFinals.ca,
              montant_ht_personnalise: montantsFinals.montant_ht,
              montant_ttc_personnalise: montantsFinals.montant_ttc,
              remise_pourcentage: montantsFinals.remise,
              remise_euros: montantsFinals.remise_euros || 0
            }
          }),
          ...(taxesFinals?.is_personnalise && {
            taxes_personnalisees: taxesFinals.detail
          }),
          ...resultat
        };

        const activitesFullDetails = getActivitesSelectionneesFull();
        
        // Rediriger vers le workflow de souscription nominative (même que annuel)
        navigate('/cooper/souscription-annuelle', {
          state: {
            tarification: tarificationMappee,
            ca_ht: montantsFinals?.ca || parseFloat(formData.ca_ht),
            nombre_jours: formData.nombre_jours ? parseInt(formData.nombre_jours) : null,
            type_souscription: 'nominative',
            activites_selectionnees: {
              gros_oeuvre: selectedActivitesGO,
              second_oeuvre: selectedActivites2nd,
              travaux_publics: selectedActivitesTP,
              pib: selectedActivitesPIB
            },
            activites_details: activitesFullDetails,
            // Données spécifiques opération nominative
            operation_nominative: {
              type_operation: operationNominative.type_operation || null,
              description_operation: operationNominative.description_operation || null,
              adresse_operation: operationNominative.adresse_operation || null,
              code_postal_operation: operationNominative.code_postal_operation || null,
              ville_operation: operationNominative.ville_operation || null,
              pays_operation: operationNominative.pays_operation || 'France',
              territoire_intervention: operationNominative.territoire_intervention || null
            },
            maitre_ouvrage: formData.maitre_ouvrage || null,
            ...(montantsFinals?.is_personnalise && {
              montants_personnalises: {
                ca_personnalise: montantsFinals.ca,
                montant_ht_personnalise: montantsFinals.montant_ht,
                montant_ttc_personnalise: montantsFinals.montant_ttc,
                remise_pourcentage: montantsFinals.remise,
                remise_euros: montantsFinals.remise_euros || 0
              }
            }),
            ...(taxesFinals?.is_personnalise && {
              taxes_personnalisees: taxesFinals.detail
            }),
            ...(isSuperAdmin && validitePersonnalisee.enabled && {
              periode_personnalisee: {
                date_debut: validitePersonnalisee.date_debut || null,
                date_fin: validitePersonnalisee.date_fin || null,
                validite_jours: validitePersonnalisee.validite_jours || 365
              }
            }),
            // Ventilation CA par territoire (panneau d'éligibilité)
            territoires_ca: eligibilityForm?.territoires_ca || {}
          }
        });
      } else {
        toast.error('Vous devez être connecté pour créer une souscription nominative');
        return;
      }
    }
    // Mode ANNUEL - COOPER'S, Admin et Super Admin
    else if (modeCalcul === 'annuel') {
      if (!user || (user.role !== 'cooper' && user.role !== 'admin' && user.role !== 'super_admin')) {
        toast.error('Vous devez être connecté en tant que COOPER\'S ou Administrateur pour créer une souscription annuelle');
        return;
      }

      // Mapper les données du résultat vers le format attendu par le workflow
      const montantsFinals = getMontantsFinals();
      const taxesFinals = getTaxesFinals();
      const tarificationMappee = {
        montant_total_ht: montantsFinals?.montant_ht || resultat.montant_ht || 0,
        montant_ht: montantsFinals?.montant_ht || resultat.montant_ht || 0,
        montant_ttc: montantsFinals?.montant_ttc || resultat.montant_ttc || 0,
        montant_ht_gros_oeuvre: resultat.detail_calcul?.rcd_solidaire_gros_oeuvre || 0,
        montant_ht_second_oeuvre: resultat.detail_calcul?.rcd_solidaire_second_oeuvre || 0,
        montant_ht_travaux_publics: resultat.detail_calcul?.rcd_solidaire_travaux_publics || 0,
        montant_ht_pib: resultat.detail_calcul?.rcd_solidaire_pib_net || 0,
        taxes: taxesFinals?.is_personnalise ? taxesFinals : (resultat.taxes || { tva: (montantsFinals?.montant_ht || resultat.montant_ht || 0) * 0.1 }),
        detail_calcul: resultat.detail_calcul || {},
        // Informations de personnalisation Super Admin
        ...(montantsFinals?.is_personnalise && {
          montants_personnalises: {
            ca_personnalise: montantsFinals.ca,
            montant_ht_personnalise: montantsFinals.montant_ht,
            montant_ttc_personnalise: montantsFinals.montant_ttc,
            remise_pourcentage: montantsFinals.remise,
            remise_euros: montantsFinals.remise_euros || 0
          }
        }),
        // Taxes personnalisées (Super Admin uniquement)
        ...(taxesFinals?.is_personnalise && {
          taxes_personnalisees: taxesFinals.detail
        }),
        // Conserver les données originales aussi
        ...resultat
      };

      // Naviguer vers la page de souscription annuelle COOPER
      // Récupérer les activités avec leurs détails complets (definition, exclusions)
      const activitesFullDetails = getActivitesSelectionneesFull();
      
      navigate('/cooper/souscription-annuelle', {
        state: {
          tarification: tarificationMappee,
          ca_ht: montantsFinals?.ca || parseFloat(formData.ca_ht),
          type_souscription: 'annuel',
          activites_selectionnees: {
            gros_oeuvre: selectedActivitesGO,
            second_oeuvre: selectedActivites2nd,
            travaux_publics: selectedActivitesTP,
            pib: selectedActivitesPIB
          },
          // Détails complets des activités (pour les documents)
          activites_details: activitesFullDetails,
          // Données du membre pré-remplies (si provenant de l'espace COOPER)
          ...(membreData && {
            membre_prefill: membreData
          }),
          // Montants personnalisés (Super Admin uniquement)
          ...(montantsFinals?.is_personnalise && {
            montants_personnalises: {
              ca_personnalise: montantsFinals.ca,
              montant_ht_personnalise: montantsFinals.montant_ht,
              montant_ttc_personnalise: montantsFinals.montant_ttc,
              remise_pourcentage: montantsFinals.remise,
              remise_euros: montantsFinals.remise_euros || 0
            }
          }),
          // Taxes personnalisées (Super Admin uniquement)
          ...(taxesFinals?.is_personnalise && {
            taxes_personnalisees: taxesFinals.detail
          }),
          // Période de validité personnalisée (Super Admin uniquement)
          ...(isSuperAdmin && validitePersonnalisee.enabled && {
            periode_personnalisee: {
              date_debut: validitePersonnalisee.date_debut || null,
              date_fin: validitePersonnalisee.date_fin || null,
              validite_jours: validitePersonnalisee.validite_jours || 365
            }
          }),
          // Ventilation CA par territoire (panneau d'éligibilité)
          territoires_ca: eligibilityForm?.territoires_ca || {}
        }
      });
    }
  };

  const handleCreerAvenant = async () => {
    if (!contratParent) {
      toast.error('Veuillez d\'abord sélectionner un contrat parent');
      return;
    }
    
    if (!resultat) {
      toast.error('Veuillez d\'abord calculer le tarif de l\'avenant');
      return;
    }
    const caAvenant = parseFloat(formData.ca_ht) || 0;
    if (resultat.ca_calcule !== undefined && Math.round(resultat.ca_calcule) !== Math.round(caAvenant)) {
      toast.error('Le CA a changé : cliquez sur « Recalculer le coût de l\'avenant » avant de créer l\'avenant');
      return;
    }
    const territoiresCAAvenant = eligibilityForm?.territoires_ca || {};
    setLoading(true);
    
    try {
      const token = getToken();
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Récupérer la date de fin du contrat parent pour l'héritage
      const dateFinContratParent = contratParent.date_fin || contratParent.periode_adhesion_fin;
      
      const avenantData = {
        montant_ht_contribution: resultat.montant_ht || 0,
        motif: 'Ajout d\'activités supplémentaires',
        notes: `CA (Montant de base): ${formData.ca_ht}€ - Activités ajoutées: GO(${selectedActivitesGO.length}), 2nd(${selectedActivites2nd.length}), PIB(${selectedActivitesPIB.length}), TP(${selectedActivitesTP.length})`,
        activites_selectionnees: {
          gros_oeuvre: selectedActivitesGO,
          second_oeuvre: selectedActivites2nd,
          travaux_publics: selectedActivitesTP,
          pib: selectedActivitesPIB
        },
        tarification: resultat,
        // CA par territoire saisi dans le simulateur Avenant (repris sur les documents)
        chiffre_affaires: caAvenant,
        territoires_ca: territoiresCAAvenant,
        // Hériter de la date de fin du contrat parent
        date_fin: dateFinContratParent
      };
      
      const contratId = contratParent.id || contratParent.numero_police;
      
      const response = await axios.post(
        `${API}/souscriptions/admin/${encodeURIComponent(contratId)}/avenant`,
        avenantData,
        { headers }
      );
      
      if (response.data.success) {
        toast.success(`Avenant ${response.data.avenant.reference} créé avec succès !`);
        
        // Préparer les données complètes pour le workflow de souscription
        const tarificationAvenant = {
          montant_total_ht: resultat.montant_ht || 0,
          montant_ht: resultat.montant_ht || 0,
          montant_ttc: resultat.montant_ttc || 0,
          montant_ht_gros_oeuvre: resultat.detail_calcul?.rcd_solidaire_gros_oeuvre || 0,
          montant_ht_second_oeuvre: resultat.detail_calcul?.rcd_solidaire_second_oeuvre || 0,
          montant_ht_travaux_publics: resultat.detail_calcul?.rcd_solidaire_travaux_publics || 0,
          montant_ht_pib: resultat.detail_calcul?.rcd_solidaire_pib_net || 0,
          taxes: resultat.taxes || { tva: (resultat.montant_ht || 0) * 0.085 },
          contributions: {
            expert_prev: 9.5,
            fogedom_scic: 9.5,
            tva: 8.5
          },
          detail_calcul: resultat.detail_calcul || {},
          ...resultat
        };
        
        // Naviguer vers le workflow avenant dédié
        // Le workflow gère: Prévisualisation DRAFT → Validation SMS → Signature → Paiement → Attestation finale
        navigate('/cooper/avenant-workflow', { 
          state: { 
            tarification: tarificationAvenant,
            ca_ht: parseFloat(formData.ca_ht),
            territoires_ca: territoiresCAAvenant,
            type_souscription: 'avenant',
            avenant_id: response.data.avenant.id,
            avenant_reference: response.data.avenant.reference,
            contrat_parent_id: contratParent.id,
            contrat_parent: contratParent,
            // Date de fin héritée du contrat parent
            date_fin_heritee: dateFinContratParent,
            // Pré-remplir les informations du membre depuis le contrat parent
            membre_info: {
              raison_sociale: contratParent.raison_sociale,
              siret: contratParent.siret,
              adresse: contratParent.adresse_entreprise,
              code_postal: contratParent.code_postal,
              ville: contratParent.ville,
              telephone: contratParent.telephone,
              email: contratParent.email,
              nom_gerant: contratParent.nom_gerant,
              prenom_gerant: contratParent.prenom_gerant,
              forme_juridique: contratParent.forme_juridique || contratParent.statut_juridique,
              date_creation_entreprise: contratParent.date_creation_entreprise,
              pays: contratParent.pays || contratParent.pays_code || 'FR',
              pays_code: contratParent.pays_code || contratParent.pays || 'FR',
              telephone_indicatif: contratParent.telephone_indicatif || '+590',
              code_naf: contratParent.code_naf,
              effectif: contratParent.effectif,
              fonction_gerant: contratParent.fonction_gerant,
              territoire: contratParent.territoire,
              territoire_id: contratParent.territoire_id
            },
            activites_selectionnees: {
              gros_oeuvre: selectedActivitesGO,
              second_oeuvre: selectedActivites2nd,
              travaux_publics: selectedActivitesTP,
              pib: selectedActivitesPIB
            }
          }
        });
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'avenant');
    } finally {
      setLoading(false);
    }
  };

  return {
    handleCreerAvenant, handleSaveForLater, handleSouscrire, handleSouscrireFromProposition,
  };
}

export default useSouscriptionActions;
